import { localeTag } from '@survey/core';
import { getLocale, t } from './i18n';

const tag = () => localeTag(getLocale());

export function formatDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(tag(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

/** Short day label for chart axes, e.g. "15 Oct" / "15 Okt". */
export function formatDay(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString(tag(), { day: 'numeric', month: 'short' });
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return t('time.just_now');
  if (min < 60) return t('time.min_ago', { n: min });
  const h = Math.round(min / 60);
  if (h < 24) return t('time.hours_ago', { n: h });
  const d = Math.round(h / 24);
  if (d < 30) return t('time.days_ago', { n: d });
  return formatDate(iso);
}

export function formatDuration(ms: number): string {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return t('dur.seconds', { s });
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? t('dur.min_sec', { m, s: rem }) : t('dur.min', { m });
  const h = Math.floor(m / 60);
  return t('dur.hour_min', { h, m: m % 60 });
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(tag()).format(n);
}

export function percent(part: number, total: number): string {
  if (!total) return '0%';
  return new Intl.NumberFormat(tag(), { style: 'percent', maximumFractionDigits: 0 }).format(part / total);
}

export function initials(name: string, email: string): string {
  const src = name.trim() || email;
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${new Intl.NumberFormat(tag(), { maximumFractionDigits: v < 10 ? 1 : 0 }).format(v)} ${units[i]}`;
}
